import React, { useCallback, useEffect, useState } from 'react';
import { Users, AlertCircle, Coins, Star, Calendar, Loader2 } from 'lucide-react';
import { apiClient } from '../../services/api';

const api = {
  dashboard: (y, m) => apiClient.get(`/hr/team/dashboard?period_year=${y}&period_month=${m}`).then((r) => r.data),
};

const TeamDashboardPage = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.dashboard(year, month)); }
    finally { setLoading(false); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4 sm:mb-6">
        <div>
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Team Dashboard</span>
          <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>My direct reports</h1>
          <p className="mt-1 text-xs sm:text-sm text-[#0b1a2e]/60">One-page view of leaves, LWP, review status and open loans.</p>
        </div>
        <div className="flex gap-2">
          <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} data-testid="team-year" className="w-24 px-2 py-1.5 border rounded text-sm" />
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} data-testid="team-month" className="px-2 py-1.5 border rounded text-sm bg-white">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('default', { month: 'short' })}</option>)}
          </select>
        </div>
      </div>
      {loading ? <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#0f5c3d] inline" /></div> : !data ? null : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <SummaryCard icon={<Users className="w-5 h-5" />} label="Team" v={data.summary.reports} />
            <SummaryCard icon={<Calendar className="w-5 h-5" />} label="Total LWP" v={`${data.summary.total_lwp}d`} accent="red" />
            <SummaryCard icon={<AlertCircle className="w-5 h-5" />} label="Pending leaves" v={data.summary.pending_leaves} accent="amber" />
            <SummaryCard icon={<Coins className="w-5 h-5" />} label="Open loans" v={data.summary.open_loans} />
            <SummaryCard icon={<Star className="w-5 h-5" />} label={`${data.quarter} reviews`} v={`${data.summary.reviews_done}/${data.summary.reports}`} accent="emerald" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {data.reports.map((r) => (
              <div key={r.user_id} className="bg-white border border-black/5 rounded-2xl overflow-hidden" data-testid={`team-card-${r.user_id}`}>
                <div className="p-4 bg-gradient-to-br from-[#0f5c3d] to-[#0b1a2e] text-white">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-lg font-bold">{(r.name || '?').charAt(0).toUpperCase()}</div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{r.name}</div>
                      <div className="text-[10px] opacity-70 truncate">{r.designation || r.employee_code || r.email}</div>
                    </div>
                  </div>
                </div>
                <div className="p-4 space-y-3 text-sm">
                  {/* Attendance */}
                  <div>
                    <div className="text-[10px] uppercase font-semibold text-[#0b1a2e]/50 mb-1">Attendance · {data.period}</div>
                    <div className="grid grid-cols-4 gap-1 text-center text-xs">
                      <Cell v={r.attendance.present} l="P" color="text-emerald-700" />
                      <Cell v={r.attendance.absent} l="A" color="text-red-600" />
                      <Cell v={r.attendance.half_day} l="H" color="text-amber-600" />
                      <Cell v={r.attendance.lwp} l="LWP" color="text-red-700" bold />
                    </div>
                  </div>
                  {/* Leaves */}
                  <div>
                    <div className="text-[10px] uppercase font-semibold text-[#0b1a2e]/50 mb-1 flex items-center justify-between">
                      <span>Leaves · YTD</span>
                      {r.leaves.pending_requests > 0 && <span className="text-amber-600 font-bold">{r.leaves.pending_requests} pending</span>}
                    </div>
                    <div className="text-xs space-y-0.5">
                      {r.leaves.used.length === 0 ? <span className="text-[#0b1a2e]/40 italic">No leaves used</span> :
                        r.leaves.used.map((l, i) => (
                          <div key={i} className="flex justify-between">
                            <span>{l.leave_type}</span>
                            <span className="tabular-nums">{l.days_used}{l.annual_quota ? `/${l.annual_quota}` : ''}</span>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                  {/* Loans */}
                  <div>
                    <div className="text-[10px] uppercase font-semibold text-[#0b1a2e]/50 mb-1">Open loans</div>
                    <div className="text-xs space-y-1">
                      {r.loans.length === 0 ? <span className="text-[#0b1a2e]/40 italic">None</span> :
                        r.loans.map((L) => (
                          <div key={L.id} className="flex justify-between items-center bg-[#f7fbf8] px-2 py-1 rounded">
                            <span className="truncate">{L.purpose || 'Loan'} {L.paused && <span className="text-amber-600 font-bold">· PAUSED</span>}</span>
                            <span className="tabular-nums font-medium">{L.outstanding.toLocaleString()}</span>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                  {/* Performance */}
                  <div>
                    <div className="text-[10px] uppercase font-semibold text-[#0b1a2e]/50 mb-1 flex items-center justify-between">
                      <span>{data.quarter} review</span>
                      {r.performance.rating != null && <span className="text-amber-500 font-bold">{r.performance.rating}★</span>}
                    </div>
                    {r.performance.status ? (
                      <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${r.performance.status === 'acknowledged' ? 'bg-emerald-100 text-emerald-800' : r.performance.status === 'shared' ? 'bg-blue-100 text-blue-800' : 'bg-slate-200'}`}>{r.performance.status}</span>
                    ) : <span className="text-[10px] text-red-600 font-bold">NOT STARTED</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {data.reports.length === 0 && <div className="p-16 text-center text-sm text-[#0b1a2e]/50 bg-white border border-black/5 rounded-2xl">No direct reports found. Set `manager_user_id` on employees under HR → Employees.</div>}
        </>
      )}
    </div>
  );
};

const SummaryCard = ({ icon, label, v, accent }) => {
  const c = { red: 'text-red-600', amber: 'text-amber-600', emerald: 'text-emerald-700' }[accent] || 'text-[#0b1a2e]';
  return (
    <div className="bg-white border border-black/5 rounded-2xl p-4">
      <div className="text-[#0f5c3d] mb-1">{icon}</div>
      <div className={`text-2xl font-bold ${c}`}>{v}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">{label}</div>
    </div>
  );
};

const Cell = ({ v, l, color, bold }) => (
  <div className="bg-[#f7fbf8] rounded p-1">
    <div className={`${bold ? 'font-bold text-lg' : 'font-semibold'} ${color}`}>{v}</div>
    <div className="text-[9px] uppercase text-[#0b1a2e]/50">{l}</div>
  </div>
);

export default TeamDashboardPage;
